import { Box, Typography } from "@mui/material";
import PropTypes from "prop-types";
import React from "react";

function StatCard({ label, value, unit, subtitle, color }) {
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: { xs: 0, md: 2 },
      }}
    >
      <Typography sx={{ fontSize: 12, color: "#667085", fontWeight: 600 }}>
        {label}
      </Typography>
      {value !== null && value !== undefined ? (
        <Box
          sx={{ display: "flex", gap: "4px", alignItems: "baseline", color }}
        >
          <Typography sx={{ fontSize: 32, fontWeight: 700, lineHeight: 1 }}>
            {value}
          </Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 600 }}>{unit}</Typography>
        </Box>
      ) : (
        <Typography sx={{ fontSize: 24, fontWeight: 600, color: "#667085" }}>
          No Data
        </Typography>
      )}
      <Typography sx={{ color: "#667085", fontSize: 12, whiteSpace: "nowrap" }}>
        {subtitle}
      </Typography>
    </Box>
  );
}

StatCard.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  unit: PropTypes.string.isRequired,
  subtitle: PropTypes.string.isRequired,
  color: PropTypes.string,
};
StatCard.defaultProps = {
  value: null,
  color: "",
};

function getTemperatureStatus(temp) {
  if (temp === null || temp === undefined) return null;

  if (temp >= 36.1 && temp <= 37.2) {
    return {
      label: "Normal (36.1-37.2)",
      color: "#00A656",
      bgColor: "#00A6560D",
      borderColor: "#00A65626",
    };
  }
  if (temp < 36.1) {
    return {
      label: "Below Normal",
      color: "#4280BE",
      bgColor: "#4280BE0D",
      borderColor: "#4280BE26",
    };
  }
  if (temp > 37.2 && temp <= 38) {
    return {
      label: "Mildly Elevated",
      color: "#FF9500",
      bgColor: "#FF95000D",
      borderColor: "#FF950026",
    };
  }
  if (temp > 38) {
    return {
      label: "Elevated",
      color: "#D32F2F",
      bgColor: "#A600000D",
      borderColor: "#A6000326",
    };
  }
  return null;
}

export default function TemperatureCard({ lastMeasures, isLoading }) {
  const temp = lastMeasures?.temperature_statistics;
  const lastSync = temp?.last_sync
    ? new Date(temp.last_sync).toLocaleString()
    : "-";

  // Use central temperature as the primary indicator
  const currentCentral = temp?.current?.central;
  const tempStatus = getTemperatureStatus(currentCentral);

  const statistics = [
    {
      label: "Central",
      value: temp?.current?.central?.toFixed(1),
      unit: "\u00B0C",
      subtitle: `Last sync: ${lastSync}`,
    },
    {
      label: "Distal",
      value: temp?.current?.distal?.toFixed(1),
      unit: "\u00B0C",
      subtitle: "Finger tip",
      color: "#4280BE",
    },
    {
      label: "Proximal",
      value: temp?.current?.proximal?.toFixed(1),
      unit: "\u00B0C",
      subtitle: "Near palm",
      color: "#FF1F56",
    },
  ];

  return (
    <Box
      sx={{
        border: "1px solid #E5E7EB",
        p: 2.5,
        borderRadius: 2,
        backgroundColor: "#FFF",
        boxShadow: "0 1px 3px rgba(0,0,0,0.1)",
        opacity: isLoading ? 0.6 : 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 2,
          flexWrap: "wrap",
          gap: 1.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            sx={{ fontWeight: 500, fontSize: 14, color: "#000000DE" }}
          >
            Temperature
          </Typography>
          {tempStatus && (
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 0.5,
                backgroundColor: tempStatus.bgColor,
                color: tempStatus.color,
                border: `1px solid ${tempStatus.borderColor}`,
                borderRadius: "8px",
                fontWeight: 500,
                fontSize: 12,
                p: "7px 8px",
              }}
            >
              <Box
                sx={{
                  width: "9px",
                  height: "9px",
                  borderRadius: "50%",
                  backgroundColor: tempStatus.color,
                }}
              />
              {tempStatus.label}
            </Box>
          )}
        </Box>
      </Box>
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: {
            xs: "repeat(2, 1fr)",
            sm: "repeat(2, 1fr)",
            lg: "repeat(3, 1fr)",
          },
          gap: 2.5,
          mb: 3,
        }}
      >
        <Box sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}>
          <StatCard {...statistics[0]} />
        </Box>
        <StatCard {...statistics[1]} />
        <StatCard {...statistics[2]} />
      </Box>
    </Box>
  );
}

TemperatureCard.propTypes = {
  lastMeasures: PropTypes.shape({
    temperature_statistics: PropTypes.shape({
      current: PropTypes.shape({
        distal: PropTypes.number,
        central: PropTypes.number,
        proximal: PropTypes.number,
      }),
      minimum: PropTypes.shape({
        distal: PropTypes.number,
        central: PropTypes.number,
        proximal: PropTypes.number,
      }),
      maximum: PropTypes.shape({
        distal: PropTypes.number,
        central: PropTypes.number,
        proximal: PropTypes.number,
      }),
      last_sync: PropTypes.string,
    }),
  }),
  isLoading: PropTypes.bool,
};

TemperatureCard.defaultProps = {
  lastMeasures: null,
  isLoading: false,
};
